import { Fade } from "@mui/material";
import React, { useState } from "react";
import CustomButton from "shared/components/button/CustomButton";
import BodyPanel from "shared/components/panel/BodyPanel";
import FooterPanel from "shared/components/panel/FooterPanel";
import HeaderPanel from "shared/components/panel/HeaderPanel";
import { Panel } from "shared/components/panel/Panel";
import SecaoConsultarNotasFiscais from "./SecaoConsultarNotasFiscais";
import { useLocation, useNavigate } from "react-router-dom";
import { FaCheck, FaX } from "react-icons/fa6";
import { usePostConfigurarGroupPay } from "hooks/groupPay/usePostConfigurarGroupPay";
import { yupResolver } from "@hookform/resolvers/yup";
import { ConfigurarConsultaNotaFiscalSchema } from "shared/yup/schemas/ConfigurarConsultaNotaFiscalSchema";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { AnexoType } from "types/anexo/AnexoType";
import { queryClient } from "shared/util/queryClient";
import { ConfigurarGroupPayRequest } from "types/request/groupPay/ConfigurarGroupPayRequest";
import { mensagemDeErroPadrao } from "shared/util/functions/yup/MensagensDeErroInput";
import showAlert from "shared/util/functions/alert/showAlert";
import { possuiMensagemDeErro } from "shared/util/functions/errors/RequestPossuiMensagemDeErro";
import { getMensagemDeErroBackend } from "shared/util/functions/errors/GetMensagemDeErroBackend";

export interface ConfigurarGroupPayForm {
  senhaPrefeitura?: string | null;
  usuarioPrefeitura?: string | null;
  senhaCertificadoDigital?: string | null;
  mailReaderEmail?: string | null;
  mailReaderPassword?: string | null;
}

const TelaConfiguracoesGroupPay: React.FC = () => {
  const [anexosCertificado, setAnexosCertificado] = useState<AnexoType[]>([]);
  const [certificadoDigitalBase64, setCertificadoDigitalBase64] = useState<string | null>("");
  const [permitirBuscaLancamentoNF, setPermitirBuscaLancamentoNF] = useState<boolean>();
  const [permitirMailReader, setPermitirMailReader] = useState<boolean>();
  const [houveAlteracaoCamposNosCampos, setHouveAlteracaoCamposNosCampos] = useState<boolean>(false);

  const location = useLocation();
  const { condominioId } = location.state;
  const navigate = useNavigate();

  const { mutateAsync } = usePostConfigurarGroupPay({successMessage: "Configuração cadastrada com sucesso!"});

  const { register, handleSubmit, control, setValue, getValues, setError, formState } = useForm<ConfigurarGroupPayForm>(
    {
      resolver: yupResolver<ConfigurarGroupPayForm>(ConfigurarConsultaNotaFiscalSchema),
    },
  );

  const enviar = async (form: ConfigurarGroupPayForm) => {
    if (isCertificadoDigitalValido() && isLoginPrefeituraValido()) {
      const configurarConsultaNotaFiscalRequest = {
        condominioId: condominioId,
        loginPrefeitura: form.usuarioPrefeitura,
        nomeCertificadoDigital: anexosCertificado[0]?.nome,
        senhaCertificadoDigital: form.senhaCertificadoDigital,
        senhaPrefeitura: form.senhaPrefeitura,
        certificadoDigitalBase64: certificadoDigitalBase64,
        permitirBuscaLancamentoNF: permitirBuscaLancamentoNF,
        readerEmail: form.mailReaderEmail,
        readerPassword: form.mailReaderPassword,
        permitirBuscaLancamentoDespesasPorLeituraEmail: permitirMailReader,
      } as ConfigurarGroupPayRequest;

      if (isConfiguracaoMailReaderValida() && isCamposConfiguracaoConsultaNotaFiscalValidos()) {
        await mutateAsync(configurarConsultaNotaFiscalRequest).catch((erro) => {
          if (possuiMensagemDeErro(erro)) {
            toast.error(getMensagemDeErroBackend(erro));
          }
        });
      }
    }
  };

  const isCamposConfiguracaoConsultaNotaFiscalValidos = () => {
    const isCamposUsuarioESenhaInvalidos =
      (getValues("usuarioPrefeitura") === null || getValues("usuarioPrefeitura") === "") &&
      (getValues("senhaPrefeitura") === null || getValues("senhaPrefeitura") === "");

    const isCamposCertficadoESenhaInvalidos =
      (getValues("senhaCertificadoDigital") === null || getValues("senhaCertificadoDigital") === "") &&
      (certificadoDigitalBase64 === null || certificadoDigitalBase64 === "");

    if (isCamposUsuarioESenhaInvalidos && isCamposCertficadoESenhaInvalidos && permitirBuscaLancamentoNF) {
      toast.error("Para prosseguir, é necessário informar o login e senha ou informar o certificado.");
      return;
    }

    return true;
  };

  const isConfiguracaoMailReaderValida = () => {
    const isEmailInvalido =
      (getValues("mailReaderEmail") === null || getValues("mailReaderEmail") === "");

    if (isEmailInvalido && permitirMailReader) {
      toast.error("Para prosseguir, é necessário preencher campos da leitura de email.");
      return;
    }

    return true;
  };

  const isLoginPrefeituraValido = () => {
    const isUsuarioPrefeituraPreenchido =
      getValues("usuarioPrefeitura") != null && getValues("usuarioPrefeitura") !== "";
    const isSenhaPrefeituraPreenchida = getValues("senhaPrefeitura") != null && getValues("senhaPrefeitura") !== "";

    if ((isUsuarioPrefeituraPreenchido || isSenhaPrefeituraPreenchida) && permitirBuscaLancamentoNF) {
      if (!isUsuarioPrefeituraPreenchido) {
        setError("usuarioPrefeitura", { message: mensagemDeErroPadrao("Usuário") });
        return false;
      }

      if (!isSenhaPrefeituraPreenchida) {
        setError("senhaPrefeitura", { message: mensagemDeErroPadrao("Senha") });
        return false;
      }
    }

    return true;
  };

  const isCertificadoDigitalValido = () => {
    const isSenhaCertificadoDigitalPreenchida =
      getValues("senhaCertificadoDigital") != null && getValues("senhaCertificadoDigital") !== "";

    const isCertificadoDigitalPreenchido = certificadoDigitalBase64 != null && certificadoDigitalBase64 !== "";

    if ((isSenhaCertificadoDigitalPreenchida || isCertificadoDigitalPreenchido) && permitirBuscaLancamentoNF) {
      if (isSenhaCertificadoDigitalPreenchida && !isCertificadoDigitalPreenchido) {
        toast.error("O certificado digital deve ser anexado");
        return false;
      }

      if (!isSenhaCertificadoDigitalPreenchida && isCertificadoDigitalPreenchido) {
        setError("senhaCertificadoDigital", { message: "É necessário informar a senha do certificado digital" });
        return false;
      }
    }

    return true;
  };

  const setarAlteracaoNosCampos = () => {
    if (!houveAlteracaoCamposNosCampos) {
      setHouveAlteracaoCamposNosCampos(true);
    }
  };

  const cancelarConfiguracoesGroupPay = () => {
    queryClient.invalidateQueries("tabela-condominios-group-pay");
    navigate(-1);
  };

  return (
    <Fade in={true} timeout={700}>
      <div>
        <Panel
          headerPanel={<HeaderPanel title="Group Pay" ocultarCondominio />}
          footerPanel={
            <FooterPanel
              title="FooterPanel"
              botoes={[
                <CustomButton
                  key={"btnVoltar"}
                  variant="danger-alt"
                  leftIcon={FaX}
                  id="btnVoltar"
                  label="Cancelar"
                  size="sm"
                  className="footer-panel-btn"
                  onClick={() => {
                    if (houveAlteracaoCamposNosCampos) {
                      showAlert({
                        texto:
                          "Existem alterações que não foram salvas na tela. Tem certeza de que deseja sair sem salvar?",
                        tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
                        onConfirm: () => cancelarConfiguracoesGroupPay(),
                      });
                    } else {
                      cancelarConfiguracoesGroupPay();
                    }
                  }}
                />,
                <CustomButton
                  key={"btnSalvarConfiguracoesGroupPay"}
                  variant="primary"
                  id="btnSalvarConfiguracoesGroupPay"
                  leftIcon={FaCheck}
                  label="Salvar"
                  size="sm"
                  className="footer-panel-btn"
                  onClick={() => handleSubmit(enviar)()}
                />,
              ]}
            />
          }
        >
          <BodyPanel>
            <>
              {/* {<SecaoConsultaDDA />} */}
              {/* {<SecaoConsultaDeCobrancaConcessionarias />} */}
              <SecaoConsultarNotasFiscais
                key={"secao-consulta-notas-fiscais"}
                control={control}
                register={register}
                setValue={setValue}
                condominioId={condominioId}
                setAnexosCertificado={setAnexosCertificado}
                setCertificadoDigitalBase64={setCertificadoDigitalBase64}
                setPermitirBuscaLancamentoNF={setPermitirBuscaLancamentoNF}
                setPermitirMailReader={setPermitirMailReader}
                onChangeValues={() => setarAlteracaoNosCampos()}
              />
              {/* {<SecaoConsultarAnexosEmail />} */}
            </>
          </BodyPanel>
        </Panel>
      </div>
    </Fade>
  );
};

export default TelaConfiguracoesGroupPay;
